/** Parent form helpers: DTO builders, hydration, validation. */
import * as React from 'react';
import {
  flattenProfessionalSituation,
  serializeProfessionalSituation,
} from '@/utils/professional-situation-utils';
import type { ParentFormValues, ParentRecord } from '@/types/parent.types';
import type {
  ArchiveReason,
  ArchiveRemoteUserDto,
  ContactKind,
  FolderSituation,
  ReactivationPreview,
} from '@/types/user.types';
import { FOLDER_SITUATION_CONFIG } from '@/constants/shared-catalog';
import type { AddressSelectionResult } from '@/types/geography.types';
import { getCompanyCountry } from '@/stores/themeStore';
import {
  formBool,
  hydratePhoneFromApi,
  hydrateUserContactFields,
  nextContactFields,
  phoneToE164,
  primaryContactKindFromType,
  assignPrimaryContactFields,
} from '@/utils/user-contact-utils';
import {
  addressFieldsFromSelection,
  assignDefined,
  assignTruthy,
  buildArchiveDto as buildSharedArchiveDto,
  fileNameFromUrl,
  formatReactivationPreview as formatSharedReactivationPreview,
  isArchiveReason as isSharedArchiveReason,
  parseDepartmentFromLabel,
} from '@/utils/common-utils';
import { PARENT_ACCOUNT_STATUS_CONFIG } from './parent-configs';
import { getErrorMessage } from '@/utils/errors-utils';
import { storedDocumentLink } from '@/utils/pending-document-link-utils';
import { uploadFile } from '@/services/files.service';

/** Contact kind alias used by parent forms. */
type ParentContactType = ContactKind;

/** User slice used when hydrating contact fields. */
export type ParentFormUser = {
  firstName?: string | null;
  lastName?: string | null;
  status?: string | null;
  email?: string | null;
  phone?: string | null;
  primaryContactKind?: 'email' | 'phone' | null;
};

/** Validation helpers used by parent form handlers. */
type ParentFormValidation = {
  validateField: (key: string, value: unknown, data: ParentFormValues) => void;
  markAsTouched: (field: string) => void;
  markAsDirty: (field: string) => void;
};

/** Archive DTO from a reason + comment. */
export function buildArchiveDto(reason: ArchiveReason, comment: string): ArchiveRemoteUserDto {
  return buildSharedArchiveDto(reason, comment);
}

/** True when the value is a known archive reason. */
export function isArchiveReason(value: string): value is ArchiveReason {
  return isSharedArchiveReason(value);
}
export type { ParentContactType };

/** Flatten onboarding sections into a single form object. */
function mergeSections(sections: Record<string, unknown> | undefined): ParentFormValues {
  let merged: ParentFormValues = {};
  for (const section of Object.values(sections ?? {})) {
    if (section && typeof section === 'object') {
      merged = { ...merged, ...(section as ParentFormValues) };
    }
  }
  return merged;
}

/** Remap contract section keys onto form field names. */
function remapContractFields(
  merged: ParentFormValues,
  sections: Record<string, unknown> | undefined,
): void {
  const contractSec = sections?.contract as ParentFormValues | undefined;
  if (contractSec?.documentLink) merged.contractDocumentLink = contractSec.documentLink;
  if (contractSec?.documentName) merged.contractDocumentName = contractSec.documentName;
  if (contractSec?.signedDate) merged.contractSignedDate = contractSec.signedDate;
  delete merged.documentLink;
  delete merged.documentName;
}

/** Overlay user identity + primary/secondary contact onto the merged form. */
function applyUserContactFields(
  merged: ParentFormValues,
  user: ParentFormUser | null | undefined,
  defaultCountry: string,
): void {
  merged.firstName = merged.firstName ?? user?.firstName ?? '';
  merged.lastName = merged.lastName ?? user?.lastName ?? '';
  merged.status = merged.status ?? user?.status ?? '';
  const hydrated = hydrateUserContactFields(
    user?.email,
    user?.phone,
    defaultCountry,
    user?.primaryContactKind,
  );
  merged.contactType = merged.contactType ?? hydrated.contactType;
  if (merged.contactType === 'phone') {
    const parsed = hydratePhoneFromApi(merged.contact || hydrated.contact, defaultCountry);
    merged.contact = parsed.value;
    merged.contactCountryCode = parsed.countryCode;
  } else {
    merged.contact = merged.contact ?? hydrated.contact;
  }
  merged.secondaryContact = hydrated.secondaryContact;
  merged.secondaryContactCountryCode = hydrated.secondaryContactCountryCode;
}

/** Legacy gender/loginCode aliases + department split + flatten pro situation. */
function normalizeParentFormValues(merged: ParentFormValues): void {
  if (merged.phoneNumber && !merged.phone) {
    merged.phone = merged.phoneNumber;
  }

  if (merged.loginCode && !merged.accessCode) {
    merged.accessCode = merged.loginCode;
  }

  if (merged.department && typeof merged.department === 'string') {
    merged.departmentId = merged.department;
    merged.departmentName = '';
  } else if (merged.department && typeof merged.department === 'object') {
    merged.departmentId = merged.department.departmentId ?? '';
    merged.departmentName = merged.department.departmentName ?? '';
  }

  if (
    merged.professionalSituation &&
    typeof merged.professionalSituation === 'object' &&
    merged.professionalSituation.status
  ) {
    merged.professionalSituation = flattenProfessionalSituation({
      ...merged.professionalSituation,
    });
  }

  merged.acceptSharePhone = formBool(merged.acceptSharePhone);
  merged.isParticipatedToStudies = formBool(merged.isParticipatedToStudies);
}

/** Empty create form; phone ISO stays on the contact, address uses the country name. */
export function emptyCreateParentForm(defaultCountry: string): ParentFormValues {
  return {
    secondaryContact: '',
    secondaryContactCountryCode: defaultCountry,
    country: getCompanyCountry(),
    acceptSharePhone: false,
    isParticipatedToStudies: false,
  };
}

/** Hydrate the edit form from a parent record. */
export function parentRecordToFormData(
  record: ParentRecord,
  defaultCountry: string,
): ParentFormValues {
  const sections = (record.sections ?? {}) as Record<string, unknown>;
  const merged = mergeSections(sections);
  remapContractFields(merged, sections);
  applyUserContactFields(merged, record.user, defaultCountry);
  normalizeParentFormValues(merged);
  if (!merged.country) merged.country = getCompanyCountry();
  return merged;
}

/** Patch one parent-form field. */
export function applyParentFormField(
  form: ParentFormValues,
  key: string,
  value: unknown,
): ParentFormValues {
  return { ...form, [key]: value };
}

/** Next email/phone fields after a contact-type change. */
export function applyParentContactChange(
  form: ParentFormValues,
  value: string,
  type: ParentContactType,
  defaultCountry: string,
  phoneCountryCode?: string,
): ParentFormValues {
  const next = nextContactFields({
    previousType: form.contactType,
    value,
    type,
    countryCode: phoneCountryCode,
    previousCountryCode: form.contactCountryCode,
    defaultCountry,
    previousSecondary: form.secondaryContact,
    previousSecondaryCountry: form.secondaryContactCountryCode,
  });
  const updates: ParentFormValues = {
    ...next,
    contactType: next.contactType as ParentContactType,
  };
  if (type === 'email') updates.email = value;
  if (type === 'phone') updates.phone = value;
  return { ...form, ...updates };
}

/** Prefer form value; otherwise keep the GET-section fallback. */
function assignDefinedOrFallback(
  dto: ParentFormValues,
  key: string,
  value: unknown,
  fallback: unknown,
): void {
  if (value !== undefined) {
    dto[key] = value;
    return;
  }
  if (fallback !== undefined) dto[key] = fallback;
}

/** Department id from `departmentId` or a string/object `department`. */
function resolveCreateDepartmentId(form: ParentFormValues): string | undefined {
  if (form.departmentId) return form.departmentId;
  if (typeof form.department === 'string') return form.department;
  return form.department?.departmentId;
}

/** Invite channel + email/phone pair from primary/secondary contact. */
function applyCreateParentContact(
  dto: ParentFormValues,
  form: ParentFormValues,
  contactType: ParentContactType,
  defaultCountry: string,
): void {
  const secondary = form.secondaryContact?.trim();
  if (contactType === 'email') {
    dto.email = form.contact;
    if (secondary) {
      dto.phone = phoneToE164(
        form.secondaryContact,
        form.secondaryContactCountryCode || defaultCountry,
      );
    }
    return;
  }
  if (contactType !== 'phone') return;
  dto.phone = phoneToE164(form.contact, form.contactCountryCode || defaultCountry);
  if (secondary) dto.email = secondary;
}

/** Nested `{ departmentId, departmentName }` for create. */
function applyCreateParentDepartment(dto: ParentFormValues, form: ParentFormValues): void {
  const createDeptId = resolveCreateDepartmentId(form);
  if (!createDeptId) return;
  dto.department = {
    departmentId: createDeptId,
    departmentName: form.departmentName || '',
  };
}

/** Build `POST /users/invite` DTO from the create form. */
export function buildCreateParentDto(
  form: ParentFormValues,
  contactType: ParentContactType,
  defaultCountry: string,
): ParentFormValues {
  const dto: ParentFormValues = {
    inviteBy: contactType === 'email' ? 'email' : 'phone',
    primaryContactKind: primaryContactKindFromType(contactType),
    firstName: form.firstName ?? '',
    lastName: form.lastName ?? '',
  };
  applyCreateParentContact(dto, form, contactType, defaultCountry);
  assignTruthy(dto, 'gender', form.gender);
  assignTruthy(dto, 'loginCode', form.accessCode);
  dto.acceptSharePhone = formBool(form.acceptSharePhone);
  assignTruthy(dto, 'address', form.address);
  assignTruthy(dto, 'zipCode', form.zipCode);
  assignTruthy(dto, 'city', form.city);
  applyCreateParentDepartment(dto, form);
  assignTruthy(dto, 'country', form.country);
  assignTruthy(dto, 'dateOfBirth', form.dateOfBirth);
  assignTruthy(
    dto,
    'professionalSituation',
    serializeProfessionalSituation(form.professionalSituation, 'parent'),
  );
  dto.isParticipatedToStudies = formBool(form.isParticipatedToStudies);
  assignTruthy(dto, 'contractDocumentLink', storedDocumentLink(form.contractDocumentLink));
  assignTruthy(dto, 'contractDocumentName', form.contractDocumentName);
  assignTruthy(dto, 'contractSignedDate', form.contractSignedDate);
  assignDefined(dto, 'contractCgu', form.contractCgu);
  assignDefined(dto, 'contractEngagement', form.contractEngagement);
  assignDefined(dto, 'contractBenevole', form.contractBenevole);
  return dto;
}

/** Named onboarding section from full-state. */
function sectionOf(
  fullState: { record?: ParentRecord } | undefined,
  key: string,
): ParentFormValues | undefined {
  return (fullState?.record?.sections as Record<string, ParentFormValues> | undefined)?.[key];
}

/** Form value wins; otherwise the GET-section value. */
function pickFormOrSection(
  form: ParentFormValues,
  section: ParentFormValues | undefined,
  key: string,
) {
  return form[key] ?? section?.[key];
}

/** Identity fields on update (name, gender, access code). */
function applyUpdateIdentity(
  dto: ParentFormValues,
  form: ParentFormValues,
  biGet: (key: string) => unknown,
): void {
  assignTruthy(dto, 'firstName', form.firstName);
  assignTruthy(dto, 'lastName', form.lastName);
  assignTruthy(dto, 'gender', biGet('gender'));
  assignTruthy(dto, 'loginCode', biGet('accessCode'));
}

/** Fill missing email/phone from the GET basic-information section. */
function applyUpdateContactFallback(
  dto: ParentFormValues,
  biBase: ParentFormValues | undefined,
): void {
  const emailVal = biBase?.email as string | undefined;
  const phoneVal = biBase?.phoneNumber as string | undefined;
  if (biBase?.email) {
    if (!dto.email && emailVal) dto.email = emailVal;
    if (!dto.phone && phoneVal) dto.phone = phoneVal;
    return;
  }
  if (!dto.phone && phoneVal) dto.phone = phoneVal;
}

/** Address + professional situation on update. */
function applyUpdatePersonal(
  dto: ParentFormValues,
  form: ParentFormValues,
  piBase: ParentFormValues | undefined,
  piGet: (key: string) => unknown,
): void {
  assignTruthy(dto, 'address', piGet('address'));
  assignTruthy(dto, 'zipCode', piGet('zipCode'));
  assignTruthy(dto, 'city', piGet('city'));
  assignTruthy(dto, 'country', piGet('country'));
  assignTruthy(dto, 'dateOfBirth', piGet('dateOfBirth'));
  const profSitVal = form.professionalSituation
    ? serializeProfessionalSituation(form.professionalSituation, 'parent')
    : piBase?.professionalSituation;
  assignTruthy(dto, 'professionalSituation', profSitVal);
  dto.isParticipatedToStudies = formBool(piGet('isParticipatedToStudies'));
}

/** Nested department on update: form id, else GET fallback. */
function applyUpdateDepartment(
  dto: ParentFormValues,
  form: ParentFormValues,
  piBase: ParentFormValues | undefined,
): void {
  const deptId = resolveCreateDepartmentId(form);
  const deptFallback = piBase?.department;
  const deptFallbackId =
    typeof deptFallback === 'string' ? deptFallback : deptFallback?.departmentId;
  const deptFallbackName = typeof deptFallback === 'object' ? deptFallback?.departmentName : '';
  const finalDeptId = deptId || deptFallbackId;
  if (!finalDeptId) return;
  dto.department = {
    departmentId: finalDeptId,
    departmentName: form.departmentName || deptFallbackName || '',
  };
}

/** Contract document + CGU flags on update (form, then GET). */
function applyUpdateContract(
  dto: ParentFormValues,
  form: ParentFormValues,
  ctBase: ParentFormValues | undefined,
): void {
  assignTruthy(
    dto,
    'contractDocumentLink',
    storedDocumentLink(form.contractDocumentLink) || ctBase?.documentLink,
  );
  assignTruthy(dto, 'contractDocumentName', form.contractDocumentName || ctBase?.documentName);
  assignTruthy(dto, 'contractSignedDate', form.contractSignedDate || ctBase?.signedDate);
  assignDefinedOrFallback(dto, 'contractCgu', form.contractCgu, ctBase?.cgu);
  assignDefinedOrFallback(dto, 'contractEngagement', form.contractEngagement, ctBase?.engagement);
  assignDefinedOrFallback(dto, 'contractBenevole', form.contractBenevole, ctBase?.benevole);
}

/** Build `PATCH` parent DTO from the edit form + GET full-state. */
export function buildUpdateParentDto(
  form: ParentFormValues,
  fullState: { record?: ParentRecord } | undefined,
  defaultCountry: string,
): ParentFormValues {
  const dto: ParentFormValues = {};
  const biBase = sectionOf(fullState, 'basic_information');
  const piBase = sectionOf(fullState, 'personal_information');
  const ctBase = sectionOf(fullState, 'contract');
  const biGet = (key: string) => pickFormOrSection(form, biBase, key);
  const piGet = (key: string) => pickFormOrSection(form, piBase, key);

  applyUpdateIdentity(dto, form, biGet);
  const contactType = resolveKnownContactType(form);
  if (contactType === 'email' || contactType === 'phone') {
    assignPrimaryContactFields({
      dto,
      primaryValue: String(form.contact ?? ''),
      primaryKind: contactType,
      primaryCountry: form.contactCountryCode,
      secondaryValue: form.secondaryContact,
      secondaryCountry: form.secondaryContactCountryCode,
      defaultCountry,
    });
  }
  dto.acceptSharePhone = formBool(biGet('acceptSharePhone'));
  applyUpdateContactFallback(dto, biBase);
  applyUpdatePersonal(dto, form, piBase, piGet);
  applyUpdateDepartment(dto, form, piBase);
  applyUpdateContract(dto, form, ctBase);
  return dto;
}

/** Digits-only access code, max 6 characters. */
export function sanitizeAccessCode(value: string): string {
  return value.replace(/\D/g, '').slice(0, 6);
}

/** Narrow unknown to a contact type; invalid → `unknown`. */
export function resolveContactType(value: unknown): ParentContactType {
  if (value === 'email' || value === 'phone' || value === 'unknown') return value;
  return 'unknown';
}

/** Email/phone only; `unknown` becomes undefined. */
export function resolveKnownContactType(form: ParentFormValues): ParentContactType | undefined {
  const next = resolveContactType(form.contactType);
  return next === 'unknown' ? undefined : next;
}

/** Hydrate form + resolved contact type from a parent record. */
export function hydrateParentFormState(
  record: ParentRecord,
  defaultCountry: string,
): { formData: ParentFormValues; contactType: ParentContactType } {
  const formData = parentRecordToFormData(record, defaultCountry);
  return { formData, contactType: resolveContactType(formData.contactType) };
}

/** Change / blur / contact handlers that keep validation in sync. */
export function useParentFormHandlers(params: {
  formData: ParentFormValues;
  setFormData: React.Dispatch<React.SetStateAction<ParentFormValues>>;
  contactType: ParentContactType;
  setContactType: (type: ParentContactType) => void;
  defaultCountry: string;
  validation: ParentFormValidation;
}) {
  const { formData, setFormData, contactType, setContactType, defaultCountry, validation } = params;

  const formDataRef = React.useRef(formData);
  formDataRef.current = formData;

  /** Set one field and re-validate with contact-type context. */
  const handleChange = (key: string, value: unknown) => {
    const updatedData = applyParentFormField(formDataRef.current, key, value);
    formDataRef.current = updatedData;
    setFormData(updatedData);
    validation.validateField(key, value, parentValidationPayload(updatedData, contactType));
    validation.markAsTouched(key);
    validation.markAsDirty(key);
  };

  /** Mark a field touched so its error can show. */
  const handleBlur = (field: string) => {
    validation.markAsTouched(field);
  };

  /** Persist primary contact and inferred type. */
  const handleContactChange = (
    value: string,
    type: ParentContactType,
    phoneCountryCode?: string,
  ) => {
    const updatedData = applyParentContactChange(
      formData,
      value,
      type,
      defaultCountry,
      phoneCountryCode,
    );
    setFormData(updatedData);
    setContactType(type);
    validation.validateField('contact', value, parentValidationPayload(updatedData, type));
    validation.markAsTouched('contact');
    validation.markAsDirty('contact');
  };

  return { handleChange, handleBlur, handleContactChange };
}

/** Form payload for the validator: `_contactType` + department alias. */
export function parentValidationPayload(
  form: ParentFormValues,
  contactType: ParentContactType,
  contactExists?: boolean | null,
  primaryContactExists?: boolean | null,
): ParentFormValues {
  return {
    ...form,
    _contactType: contactType,
    _contactExists: primaryContactExists ?? contactExists,
    department: form.departmentId || form.department,
  };
}

/** Fill address / department from an autocomplete pick. */
export function applyParentAddressSelection(
  onChange: (key: string, value: unknown) => void,
  result: AddressSelectionResult,
): void {
  const patch = parentAddressFromAutocomplete(result);
  for (const [key, val] of Object.entries(patch)) {
    onChange(key, val);
  }
}

/** Address-autocomplete result → form patch (BAN `depcode` or `"Name (Code)"`). */
export function parentAddressFromAutocomplete(
  result: AddressSelectionResult,
): Partial<ParentFormValues> {
  const parsed = result.department ? parseDepartmentFromLabel(result.department) : null;
  const departmentId = result.departmentCode || parsed?.code;
  return {
    ...addressFieldsFromSelection(result),
    ...(departmentId ? { departmentId, departmentName: parsed?.name ?? '' } : {}),
  };
}

/** Form patch for a contract upload (or clear when `url` is empty). */
export function parentContractDocumentPatch(url: string): Partial<ParentFormValues> {
  if (!url) {
    // Clear: wipe metadata so validation treats the document as missing.
    return {
      contractDocumentLink: '',
      contractDocumentName: '',
      contractUploadDate: '',
    };
  }
  return {
    contractDocumentLink: url,
    contractDocumentName: fileNameFromUrl(url),
    contractUploadDate: new Date().toISOString().slice(0, 10),
  };
}

/** Generic parent-form file upload; returns the stored URL. */
export function defaultParentUpload(file: File): Promise<string> {
  return uploadFile(file);
}

/** Catalog label for a reactivation status code. */
export function getReactivationStatusLabel(status: string): string {
  return PARENT_ACCOUNT_STATUS_CONFIG[status]?.label ?? status;
}

/** Catalog label for a reactivation situation, or `'—'`. */
export function getReactivationSituationLabel(situation: string | null): string {
  if (!situation) return '—';
  return FOLDER_SITUATION_CONFIG[situation as FolderSituation]?.label ?? situation;
}

/** Current vs next status/situation labels for the reactivation confirm. */
export function formatReactivationPreview(preview: ReactivationPreview): {
  currentStatus: string;
  nextStatus: string;
  currentSituation: string;
  nextSituation: string;
} {
  return formatSharedReactivationPreview(
    preview,
    getReactivationStatusLabel,
    getReactivationSituationLabel,
  );
}

/** Catalogued copy when archiving also archives children. */
export function getArchiveCascadeChildrenMessage(count: number): string {
  return getErrorMessage({ code: 'archiveCascadeChildren', count });
}

/** Shallow-copy the form, then write the uploaded contract URL when present. */
export function mergePendingUploads<T extends { contractDocumentLink?: string }>(
  formData: T,
  uploadedUrls: Record<string, string>,
): T {
  return {
    ...formData,
    ...(uploadedUrls.contractFile ? { contractDocumentLink: uploadedUrls.contractFile } : {}),
  };
}
